import { Modal, StatusEnum, StatusUI } from '@brandbank/portal-components';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { MenuItem } from '@mui/material';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { Select } from 'formik-mui';
import { useRef } from 'react';
import * as yup from 'yup';
import { useGetGlobalPurposeIntentsList } from '../api';
import {
  PurposeIntentTag,
  UpdatePurposeIntentPayload,
} from '../purposeIntent.types';

type AddPurposeIntentModalProps = {
  purposeId: string;
  currentTags: PurposeIntentTag[];
};

export default NiceModal.create(
  ({ purposeId, currentTags }: AddPurposeIntentModalProps) => {
    const { hide, remove, visible, resolve } = useModal();

    const typeSafeResolve = (value: UpdatePurposeIntentPayload | null) => {
      resolve(value);
    };

    const INITIAL_VALUES: UpdatePurposeIntentPayload = {
      action: 'add',
      purposeId,
      tagId: '',
    };

    const formRef = useRef<HTMLFormElement | null>(null);

    const formId = 'add-purpose-intent-to-purpose-form';

    const addPurposeIntentModalSchema = yup.object({
      tagId: yup
        .string()
        .typeError('Tag is required.')
        .required('Tag is required.')
        .test(
          'tag-not-already-selected',
          `This tag is already selected for this feed.`,
          (value) => {
            const currentTagIds = currentTags?.map((tag) => tag.tagId) || [];

            if (currentTagIds.includes(value as string)) return false;

            return true;
          }
        ),
    });

    const onSubmit = (
      values: UpdatePurposeIntentPayload,
      formikHelpers: FormikHelpers<UpdatePurposeIntentPayload>
    ) => {
      formikHelpers.setSubmitting(false);

      typeSafeResolve(values);
      hide();
      return;
    };

    const onCancel = () => {
      typeSafeResolve(null);
      hide();
    };

    const { data, isLoading, status, refetch } =
      useGetGlobalPurposeIntentsList();

    return (
      <Formik
        initialValues={INITIAL_VALUES}
        onSubmit={onSubmit}
        validationSchema={addPurposeIntentModalSchema}
      >
        {({ handleChange, handleSubmit }) => (
          <Modal
            action={
              <>
                <Button onClick={onCancel}>Cancel</Button>
                <Button type='submit' variant='contained' form={formId}>
                  Add
                </Button>
              </>
            }
            muiTransitionProps={{ onExited: remove }}
            open={visible}
            onClose={hide}
            title={`Add Feed Intent Tag`}
            width='30%'
          >
            <Form id={formId} onSubmit={handleSubmit} ref={formRef}>
              <Stack spacing={2}>
                {status !== 'error' && (
                  <Field
                    component={Select}
                    fullWidth
                    label='Tag'
                    name='tagId'
                    onChange={handleChange}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: '15rem',
                        },
                      },
                    }}
                  >
                    {isLoading ? (
                      <MenuItem disabled>Tag List Loading...</MenuItem>
                    ) : (
                      (data?.intentTags || []).map((tag, idx) => (
                        <MenuItem
                          key={`tag-option-${idx}`}
                          value={tag.tagId}
                          disabled={tag.isDeleted}
                        >
                          {tag.description}
                        </MenuItem>
                      ))
                    )}
                  </Field>
                )}

                {status === 'error' && (
                  <StatusUI
                    status={StatusEnum.ERROR}
                    subtitle='Something went wrong fetching tag options'
                    action={
                      <Button onClick={() => refetch()}>Try Again</Button>
                    }
                  />
                )}
              </Stack>
            </Form>
          </Modal>
        )}
      </Formik>
    );
  }
);
